import { resolveMessengerV2NotificationBody } from '../../../lib/messengerDesktopNotifications.js';

const toStringOrNull = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

// The latest decrypted control event is separate from the visible message timeline.
export const resolveMessengerV2LatestReactionPreview = (timeline = {}, formatMessage = null) => {
  const activity = timeline?.latestActivity;
  if (timeline?.cleared || activity?.kind !== 'reaction' || activity.action === 'remove' || !activity.emoji) return null;
  const activityTime = Date.parse(activity.createdAt);
  const hasNewerMessage = (timeline.items || []).some(item =>
    Number(activity.serverSeq) > 0 && Number(item.serverSeq ?? item.server_seq) > 0
      ? Number(item.serverSeq ?? item.server_seq) > Number(activity.serverSeq)
      : (Date.parse(item.createdAt) || 0) > activityTime);
  if (!Number.isFinite(activityTime) || hasNewerMessage) return null;
  const message = toStringOrNull(activity.targetPreview)?.replace(/\s+/g, ' ').slice(0, 120);
  const descriptor = message
    ? { id: 'messenger_v2.conversation.reaction_preview', defaultMessage: 'Reacted {emoji} to “{message}”' }
    : { id: 'messenger_v2.conversation.reaction_preview_unknown', defaultMessage: 'Reacted {emoji} to a message' };
  const values = { emoji: activity.emoji, message };
  return typeof formatMessage === 'function' ? formatMessage(descriptor, values)
    : descriptor.defaultMessage.replace('{emoji}', values.emoji).replace('{message}', message || '');
};

const resolveParticipant = (conversation = null, participantId = null) => {
  const normalizedParticipantId = toStringOrNull(participantId);
  const participants = Array.isArray(conversation?.participants)
    ? conversation.participants
    : Array.isArray(conversation?.users)
      ? conversation.users
      : [];
  return (
    participants.find((candidate) =>
      [
        candidate?.id,
        candidate?.user_id,
        candidate?.userId,
        candidate?.slug,
        candidate?.user_slug,
        candidate?.userSlug,
        candidate?.username,
        candidate?.handle,
        candidate?.user?.id,
        candidate?.user?.slug,
        candidate?.user?.user_slug,
        candidate?.user?.userSlug,
        candidate?.user?.username,
        candidate?.user?.handle,
      ]
        .map(toStringOrNull)
        .filter(Boolean)
        .includes(normalizedParticipantId),
    ) || null
  );
};

export const resolveMessengerV2ReactionNotification = ({
  activity = null,
  conversation = null,
  currentUserId = null,
  formatMessage = null,
} = {}) => {
  if (
    activity?.kind !== 'reaction' ||
    activity?.action === 'remove' ||
    activity?.targetMine !== true ||
    toStringOrNull(activity?.authorId) === toStringOrNull(currentUserId)
  ) {
    return null;
  }

  const emoji = toStringOrNull(activity?.emoji);
  if (!emoji) return null;

  const participant = resolveParticipant(conversation, activity.authorId);
  const participantName = toStringOrNull(
    participant?.full_name ??
      participant?.fullName ??
      participant?.name ??
      participant?.user_name ??
      participant?.userName ??
      participant?.username ??
      participant?.user?.full_name ??
      participant?.user?.fullName ??
      participant?.user?.name ??
      participant?.user?.user_name ??
      participant?.user?.userName ??
      participant?.user?.username ??
      null,
  );
  const avatarUrl = toStringOrNull(
    participant?.avatar_url ??
      participant?.avatarUrl ??
      participant?.avatar ??
      participant?.image_url ??
      participant?.imageUrl ??
      participant?.image ??
      participant?.user?.avatar_url ??
      participant?.user?.avatarUrl ??
      participant?.user?.avatar ??
      null,
  );
  const fallbackNameDescriptor = {
    id: 'messenger_v2.common.someone',
    defaultMessage: 'Someone',
  };
  const name =
    participantName ||
    (typeof formatMessage === 'function'
      ? formatMessage(fallbackNameDescriptor)
      : fallbackNameDescriptor.defaultMessage);
  const targetPreview = resolveMessengerV2NotificationBody({
    preview: activity?.targetPreview,
    fallbackLabel: '',
  });
  const bodyDescriptor = targetPreview
    ? {
        id: 'messenger_v2.notifications.reaction_body_with_preview',
        defaultMessage: 'Reacted {emoji} to your message: “{message}”',
      }
    : {
        id: 'messenger_v2.notifications.reaction_body',
        defaultMessage: 'Reacted {emoji} to your message.',
      };
  const values = { emoji, message: targetPreview };
  let body = bodyDescriptor.defaultMessage;
  if (typeof formatMessage === 'function') {
    body = formatMessage(bodyDescriptor, values);
  } else {
    body = body.replace('{emoji}', emoji).replace('{message}', targetPreview || '');
  }
  return { avatarUrl, body, title: name };
};

export default resolveMessengerV2ReactionNotification;
